import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Loading } from '../../../components/loading/Loading';
import { formatDate, formatDateTime } from '../../../common/formatDateText';
import { getUserById } from '../../../redux/actions/users';
import { PopoverCheckboxFilter } from '../../../components/popoverCheckboxFilter/PopoverCheckboxFilter';
import { PopoverRadioFilter } from '../../../components/popoverRadioFilter/PopoverRadioFilter';
import {
    UserIcon,
    CreditCardIcon,
    DocumentTextIcon,
    ExclamationTriangleIcon,
    ChatBubbleLeftRightIcon,
    Squares2X2Icon,
    ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import { categoryOptions, sortOptions, levelEducation } from '../../../static/data';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export const TabActivity = () => {
    const dispatch = useDispatch();
    const { userSelected, loading } = useSelector((state) => state.users);

    // Estados para filtros y paginación
    const [sortOrder, setSortOrder] = useState('desc');
    const [categoryFilter, setCategoryFilter] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Obtener records y metadata del usuario
    const records = userSelected?.records || [];
    const recordsMetadata = userSelected?.recordsMetadata || { total: 0, page: 1, limit: 10, hasMore: false };

    // Cargar datos iniciales cuando cambian los filtros
    useEffect(() => {
        if (userSelected?._id) {
            setCurrentPage(1);
            dispatch(getUserById(userSelected._id, 1, 10, sortOrder, categoryFilter));
        }
    }, [sortOrder, categoryFilter, userSelected?._id, dispatch]);

    // Adaptador para que PopoverCheckboxFilter trabaje contra un estado plano de array
    const categoryFilterState = { category: categoryFilter };
    const setCategoryFilterState = (next) => {
        setCategoryFilter(Array.isArray(next?.category) ? next.category : []);
    };

    // Función para cargar más records
    const handleLoadMore = async () => {
        if (!userSelected?._id || isLoadingMore) return;

        setIsLoadingMore(true);
        const nextPage = currentPage + 1;

        try {
            const response = await dispatch(getUserById(userSelected._id, nextPage, 10, sortOrder, categoryFilter));
            if (response?.user) {
                setCurrentPage(nextPage);
            }
        } catch (error) {
            console.error('Error loading more records:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    // Función para obtener colores según categoría
    const getCategoryColors = (category) => {
        switch (category) {
            case 'user':
                return {
                    bg: 'bg-blue-50',
                    iconBg: 'bg-blue-100',
                    icon: 'text-blue-600',
                    dot: 'bg-blue-400'
                };
            case 'payment':
                return {
                    bg: 'bg-emerald-50',
                    iconBg: 'bg-emerald-100',
                    icon: 'text-emerald-600',
                    dot: 'bg-emerald-400'
                };
            case 'request':
                return {
                    bg: 'bg-amber-50',
                    iconBg: 'bg-amber-100',
                    icon: 'text-amber-600',
                    dot: 'bg-amber-400'
                };
            case 'logs':
                return {
                    bg: 'bg-rose-50',
                    iconBg: 'bg-rose-100',
                    icon: 'text-rose-600',
                    dot: 'bg-rose-400'
                };
            case 'nota':
                return {
                    bg: 'bg-violet-50',
                    iconBg: 'bg-violet-100',
                    icon: 'text-violet-600',
                    dot: 'bg-violet-400'
                };
            default:
                return {
                    bg: 'bg-gray-50',
                    iconBg: 'bg-gray-100',
                    icon: 'text-gray-600',
                    dot: 'bg-gray-400'
                };
        }
    };

    // Función para obtener icono según categoría
    const getCategoryIcon = (category) => {
        switch (category) {
            case 'user':
                return <UserIcon className="w-5 h-5" />;
            case 'payment':
                return <CreditCardIcon className="w-5 h-5" />;
            case 'request':
                return <DocumentTextIcon className="w-5 h-5" />;
            case 'logs':
                return <ExclamationTriangleIcon className="w-5 h-5" />;
            case 'nota':
                return <ChatBubbleLeftRightIcon className="w-5 h-5" />;
            default:
                return <DocumentTextIcon className="w-5 h-5" />;
        }
    };

    // Función para formatear el nombre de usuario
    const formatUserName = (user) => {
        if (!user) return 'N/A';
        if (typeof user === 'string') return user; // Si es solo un ID
        const { firstName, secondName, lastName, secondSurname } = user;
        return [firstName, secondName, lastName, secondSurname]
            .filter(Boolean)
            .map(name => name ? name.charAt(0).toUpperCase() + name.slice(1) : '')
            .join(' ') || 'N/A';
    };

    // Función auxiliar para obtener el label de education
    const getEducationLabel = (value) => {
        if (!value || value === 'N/A' || value === '') return value || 'N/A';
        const educationOption = levelEducation.find(opt => opt.value === value);
        return educationOption ? educationOption.description : value;
    };

    // Función auxiliar para obtener los estilos del status
    const getStatusStyles = (status) => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            case 'activo':
                return {
                    bg: 'bg-emerald-100',
                    text: 'text-emerald-500',
                    dot: 'bg-emerald-500'
                };
            case 'inactivo':
                return {
                    bg: 'bg-gray-100',
                    text: 'text-gray-500',
                    dot: 'bg-gray-500'
                };
            case 'suspendido':
                return {
                    bg: 'bg-orange-100',
                    text: 'text-orange-500',
                    dot: 'bg-orange-500'
                };
            case 'baja':
                return {
                    bg: 'bg-rose-100',
                    text: 'text-rose-500',
                    dot: 'bg-rose-500'
                };
            default:
                return {
                    bg: 'bg-gray-100',
                    text: 'text-gray-500',
                    dot: 'bg-gray-500'
                };
        }
    };

    // Función auxiliar para obtener los estilos del statusSubscription
    const getStatusSubscriptionStyles = (status) => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            case 'pagado':
                return {
                    bg: 'bg-emerald-100',
                    text: 'text-emerald-500',
                    dot: 'bg-emerald-500'
                };
            case 'vencida':
            case 'vencido':
                return {
                    bg: 'bg-rose-100',
                    text: 'text-rose-500',
                    dot: 'bg-rose-500'
                };
            default:
                return {
                    bg: 'bg-gray-100',
                    text: 'text-gray-500',
                    dot: 'bg-gray-500'
                };
        }
    };

    // Función auxiliar para renderizar el badge de status
    const renderStatusBadge = (status) => {
        if (!status || status === 'N/A' || status === '') return <span className="font-bold align-baseline">N/A</span>;

        const styles = getStatusStyles(status);
        const statusCapitalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

        return (
            <>
                {/* Badge de status */}
                <span className={`inline-flex items-baseline gap-2 ${styles.bg} ${styles.text} text-[.8rem] font-semibold px-3 py-[2px] rounded-md shadow-sm align-baseline`}>
                    <span className='relative flex h-2 w-2 items-center justify-center self-center'>
                        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${styles.dot} opacity-75`}></span>
                        <span className={`relative inline-flex rounded-full h-2 w-2 ${styles.dot}`}></span>
                    </span>
                    <span className="align-baseline">{statusCapitalized}</span>
                </span>
            </>
        );
    };

    // Función auxiliar para renderizar el badge de statusSubscription
    const renderStatusSubscriptionBadge = (status) => {
        if (!status || status === 'N/A' || status === '') return <span className="font-bold align-baseline">N/A</span>;

        const styles = getStatusSubscriptionStyles(status);
        const statusCapitalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

        return (
            <>
                {/* Badge de statusSubscription */}
                <span className={`inline-flex items-baseline gap-2 ${styles.bg} ${styles.text} text-[.8rem] font-semibold px-3 py-[2px] rounded-md shadow-sm align-baseline`}>
                    <span className='relative flex h-2 w-2 items-center justify-center self-center'>
                        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${styles.dot} opacity-75`}></span>
                        <span className={`relative inline-flex rounded-full h-2 w-2 ${styles.dot}`}></span>
                    </span>
                    <span className="align-baseline">{statusCapitalized}</span>
                </span>
            </>
        );
    };

    // Función auxiliar para detectar si un valor es una fecha y formatearla
    const formatValueIfDate = (value, field) => {
        if (!value || value === 'N/A' || value === '') return value || 'N/A';

        // Lista de campos que son fechas (solo día; sin hora en el texto del cambio)
        const dateFields = [
            'paymentDeadlineDate',
            'paymentReceiptDate',
            'dateBirth',
            'lastDatePayment',
            'createdAt',
            'updatedAt',
            'date',
        ];

        // Si el campo es una fecha conocida, intentar formatear
        if (dateFields.includes(field)) {
            try {
                const date = new Date(value);
                if (!Number.isNaN(date.getTime())) {
                    return formatDate(date);
                }
            } catch (e) {
                return value;
            }
        }

        // Si el valor parece ser una fecha ISO (contiene 'T' o es un formato ISO)
        if (typeof value === 'string' && (value.includes('T') || value.match(/^\d{4}-\d{2}-\d{2}/))) {
            try {
                const date = new Date(value);
                if (!Number.isNaN(date.getTime())) {
                    return formatDate(date);
                }
            } catch (e) {
                // ignorar
            }
        }

        return value;
    };

    // Función para obtener los estilos del tipo de pago
    const getPaymentTypeStyles = (paymentType) => {
        const typeLower = paymentType?.toLowerCase() || '';
        switch (typeLower) {
            case 'suscripcion':
                return {
                    bg: 'bg-sky-100',
                    text: 'text-sky-500',
                    dot: 'bg-sky-500'
                };
            case 'pre registro':
                return {
                    bg: 'bg-purple-100',
                    text: 'text-purple-500',
                    dot: 'bg-purple-500'
                };
            default:
                return {
                    bg: 'bg-gray-100',
                    text: 'text-gray-500',
                    dot: 'bg-gray-500'
                };
        }
    };

    // Función para formatear el tipo de pago
    const formatPaymentType = (paymentType) => {
        if (!paymentType) return '';
        const types = {
            'suscripcion': 'Suscripción',
            'pre registro': 'Pre registro'
        };
        return types[paymentType] || paymentType;
    };

    // Función auxiliar para renderizar el badge de tipo de pago
    const renderPaymentTypeBadge = (paymentType) => {
        if (!paymentType) return <span className="font-bold align-baseline">pago</span>;

        const styles = getPaymentTypeStyles(paymentType);
        const typeFormatted = formatPaymentType(paymentType);

        return (
            <>
                {/* Badge de tipo de pago */}
                <span className={`inline-flex items-baseline gap-2 ${styles.bg} ${styles.text} text-[.8rem] font-semibold px-3 py-[2px] rounded-md shadow-sm align-baseline`}>
                    {/* <span className='relative flex h-2 w-2 items-center justify-center self-center'>
                        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${styles.dot} opacity-75`}></span>
                        <span className={`relative inline-flex rounded-full h-2 w-2 ${styles.dot}`}></span>
                    </span> */}
                    <span className="align-baseline">{typeFormatted}</span>
                </span>
            </>
        );
    };

    // Función para generar descripción del movimiento con formato HTML
    const getMovementDescription = (record) => {
        const { category, action, field, previousValue, newValue, courseAdded, courseDeleted, numberPayment, paymentAmount, paymentType } = record;
        const byWhoName = formatUserName(record.byWho);

        switch (category) {
            case 'user':
                if (action === 'create') {
                    const createdUserName = record.userAffected ? formatUserName(record.userAffected) : 'al usuario';
                    return (
                        <>
                            <span className="font-bold">{byWhoName}</span> creó el usuario{' '}
                            <span className="font-bold">{createdUserName}</span>
                        </>
                    );
                } else if (action === 'update' && field) {
                    const fieldNames = {
                        firstName: 'Nombre',
                        secondName: 'Segundo nombre',
                        lastName: 'Apellido paterno',
                        secondSurname: 'Apellido materno',
                        phone: 'Teléfono',
                        location: 'Ubicación',
                        zipCode: 'Codigo postal',
                        paymentDeadlineDate: 'Fecha de vencimiento',
                        dateBirth: 'Fecha de nacimiento',
                        lastDatePayment: 'Última fecha de pago',
                        lastPaymentAmount: 'Ultimo pago',
                        education: 'Nivel educativo',
                        status: 'Estatus del usuario',
                        statusSubscription: 'Estado de suscripción',
                        paidWeeks: 'Semanas pagadas',
                        subscriptionPayment: 'Monto de suscripción',
                        emailTax: 'Correo electronico fiscal',
                        rfc: 'Registro fiscal de contribuyentes',
                        businessName: 'Razon social',
                        taxRegime: 'Regimen fiscal',
                        paymentReceiptDate: 'Fecha del recibo',
                    };
                    const fieldName = fieldNames[field] || field;
                    // Formatear valores según el tipo de campo
                    let formattedPreviousValue = previousValue || 'N/A';
                    let formattedNewValue = newValue || 'N/A';

                    // Si es status, renderizar badges con estilos
                    if (field === 'status') {
                        return (
                            <>
                                <span className="font-bold">{byWhoName}</span> actualizó{' '}
                                <span className="font-bold">{fieldName}</span> de{' '}
                                {renderStatusBadge(previousValue)} a{' '}
                                {renderStatusBadge(newValue)}
                            </>
                        );
                    }

                    // Si es statusSubscription, renderizar badges con estilos
                    if (field === 'statusSubscription') {
                        return (
                            <>
                                <span className="font-bold">{byWhoName}</span> actualizó{' '}
                                <span className="font-bold">{fieldName}</span> de{' '}
                                {renderStatusSubscriptionBadge(previousValue)} a{' '}
                                {renderStatusSubscriptionBadge(newValue)}
                            </>
                        );
                    }

                    // Si es education, usar el label
                    if (field === 'education') {
                        formattedPreviousValue = getEducationLabel(previousValue);
                        formattedNewValue = getEducationLabel(newValue);
                    } else if (field === 'subscriptionPayment' || field === 'lastPaymentAmount') {
                        // Si es subscriptionPayment o lastPaymentAmount, formatear como moneda
                        formattedPreviousValue = `$${parseFloat(previousValue || 0).toLocaleString('es-MX')}`;
                        formattedNewValue = `$${parseFloat(newValue || 0).toLocaleString('es-MX')}`;
                    } else {
                        formattedPreviousValue = formatValueIfDate(previousValue, field);
                        formattedNewValue = formatValueIfDate(newValue, field);
                    }

                    return (
                        <>
                            <span className="font-bold">{byWhoName}</span> actualizó{' '}
                            <span className="font-bold">{fieldName}</span> de{' '}
                            <span className="font-bold">{formattedPreviousValue}</span> a{' '}
                            <span className="font-bold">{formattedNewValue}</span>
                        </>
                    );
                } else if (action === 'add' && courseAdded) {
                    const courseName = typeof courseAdded === 'object' ? courseAdded.identifier || courseAdded._id : courseAdded;
                    const addedStudentName = record.userAffected ? formatUserName(record.userAffected) : null;
                    return (
                        <>
                            <span className="font-semibold md:font-bold">{byWhoName}</span> agregó{' '}
                            {addedStudentName && <><span className="font-semibold md:font-bold">{addedStudentName}</span>{' '}</>}
                            al curso{' '}
                            <span className="font-semibold md:font-bold">{courseName}</span>
                        </>
                    );
                } else if (action === 'delete' && courseDeleted) {
                    const courseName = typeof courseDeleted === 'object' ? courseDeleted.identifier || courseDeleted._id : courseDeleted;
                    const deletedStudentName = record.userAffected ? formatUserName(record.userAffected) : null;
                    return (
                        <>
                            <span className="font-semibold md:font-bold">{byWhoName}</span> eliminó{' '}
                            {deletedStudentName && <><span className="font-semibold md:font-bold">{deletedStudentName}</span>{' '}</>}
                            del curso{' '}
                            <span className="font-semibold md:font-bold">{courseName}</span>
                        </>
                    );
                }
                break;
            case 'payment':
                if (action === 'create') {
                    return (
                        <>
                            <span className="font-bold">{byWhoName}</span> creó un pago de{' '}
                            {renderPaymentTypeBadge(paymentType)}{' '}
                            <span className="font-bold">#{numberPayment || 'N/A'}</span> por{' '}
                            <span className="font-bold">${paymentAmount?.toLocaleString('es-MX') || '0'}</span>
                        </>
                    );
                } else if (action === 'validate') {
                    const validatedByName = record.validatedBy ? formatUserName(record.validatedBy) : 'Alguien';
                    return (
                        <>
                            <span className="font-bold">{validatedByName}</span> validó el pago de{' '}
                            {renderPaymentTypeBadge(paymentType)}{' '}
                            <span className="font-bold">#{numberPayment || 'N/A'}</span> por{' '}
                            <span className="font-bold">${paymentAmount?.toLocaleString('es-MX') || '0'}</span>
                        </>
                    );
                } else if (action === 'reject') {
                    return (
                        <>
                            <span className="font-bold">{byWhoName}</span> rechazó el pago de{' '}
                            {renderPaymentTypeBadge(paymentType)}{' '}
                            <span className="font-bold">#{numberPayment || 'N/A'}</span> por{' '}
                            <span className="font-bold">${paymentAmount?.toLocaleString('es-MX') || '0'}</span>
                        </>
                    );
                } else if (action === 'update' && field) {
                    const fieldNames = {
                        amount: 'Monto del pago',
                        account: 'Cuenta',
                        comprobante: 'Comprobante de pago',
                        comprobante_url: 'Enlace del comprobante',
                        urlName: 'Comprobante',
                        status: 'Estado',
                        paymentReceiptDate: 'Fecha del recibo',
                    };
                    const fieldName = fieldNames[field] || field;

                    // Si es comprobante, mostrar mensaje simple sin valores
                    if (field === 'comprobante') {
                        return (
                            <>
                                <span className="font-bold">{byWhoName}</span> actualizó el{' '}
                                <span className="font-bold">{fieldName}</span> del pago de{' '}
                                {renderPaymentTypeBadge(paymentType)}{' '}
                                <span className="font-bold">#{numberPayment || 'N/A'}</span>
                            </>
                        );
                    }

                    // Formatear valores según el tipo de campo
                    let formattedPreviousValue = previousValue || 'N/A';
                    let formattedNewValue = newValue || 'N/A';

                    if (field === 'amount') {
                        formattedPreviousValue = `$${parseFloat(previousValue || 0).toLocaleString('es-MX')}`;
                        formattedNewValue = `$${parseFloat(newValue || 0).toLocaleString('es-MX')}`;
                    } else {
                        formattedPreviousValue = formatValueIfDate(previousValue, field);
                        formattedNewValue = formatValueIfDate(newValue, field);
                    }

                    return (
                        <>
                            <span className="font-bold">{byWhoName}</span> actualizó{' '}
                            <span className="font-bold">{fieldName}</span> del pago de{' '}
                            {renderPaymentTypeBadge(paymentType)}{' '}
                            <span className="font-bold">#{numberPayment || 'N/A'}</span> de{' '}
                            <span className="font-bold">{formattedPreviousValue}</span> a{' '}
                            <span className="font-bold">{formattedNewValue}</span>
                        </>
                    );
                }
                break;
            case 'request':
                // Verificar si es una solicitud de factura (typeRequest === 'invoice')
                const isInvoiceRequest = record.typeRequest === 'invoice';
                const requestNumber = field; // El número de request está en el campo 'field' para facturas

                // Obtener información del curso si está disponible (solo para solicitudes de cursos)
                const courseInfo = courseAdded || courseDeleted;
                const courseName = courseInfo
                    ? (typeof courseInfo === 'object' ? courseInfo.identifier || courseInfo._id : courseInfo)
                    : null;

                // Obtener el nombre del estudiante afectado
                const studentName = record.userAffected ? formatUserName(record.userAffected) : null;

                // Determinar el tipo de acción basado en el field o en courseAdded/courseDeleted
                const requestAction = field && !isInvoiceRequest ? field : (courseAdded ? 'add' : courseDeleted ? 'delete' : null);

                // Solicitud de factura
                if (isInvoiceRequest) {
                    if (action === 'create') {
                        return (
                            <>
                                <span className="font-semibold md:font-bold">{byWhoName}</span> creó una solicitud de factura{' '}
                                {requestNumber && <><span className="font-semibold md:font-bold">#{requestNumber}</span></>}
                            </>
                        );
                    } else if (action === 'validate') {
                        const validatedByName = record.validatedBy ? formatUserName(record.validatedBy) : 'Alguien';
                        return (
                            <>
                                <span className="font-semibold md:font-bold">{validatedByName}</span> validó una solicitud de factura{' '}
                                {requestNumber && <><span className="font-semibold md:font-bold">#{requestNumber}</span></>}
                            </>
                        );
                    } else if (action === 'reject') {
                        return (
                            <>
                                <span className="font-semibold md:font-bold">{byWhoName}</span> rechazó una solicitud de factura{' '}
                                {requestNumber && <><span className="font-semibold md:font-bold">#{requestNumber}</span></>}
                            </>
                        );
                    }
                }

                // Solicitud de cambio de curso
                if (action === 'create' && requestAction && courseName) {
                    const actionText = requestAction === 'add' ? 'agregar al estudiante' : requestAction === 'delete' ? 'eliminar al estudiante' : 'solicitud para';
                    return (
                        <>
                            <span className="font-semibold md:font-bold">{byWhoName}</span> creó una solicitud para{' '}
                            <span >{actionText}</span>{' '}
                            {studentName && <><span className="font-semibold md:font-bold">{studentName}</span>{' '}</>}
                            del curso{' '}
                            <span className="font-semibold md:font-bold">{courseName}</span>
                        </>
                    );
                } else if (action === 'validate' && !isInvoiceRequest) {
                    const validatedByName = record.validatedBy ? formatUserName(record.validatedBy) : 'Alguien';
                    return (
                        <>
                            <span className="font-semibold md:font-bold">{validatedByName}</span> validó una solicitud
                        </>
                    );
                } else if (action === 'reject' && !isInvoiceRequest) {
                    return (
                        <>
                            <span className="font-semibold md:font-bold">{byWhoName}</span> rechazó una solicitud
                        </>
                    );
                } else {
                    return (
                        <>
                            <span className="font-semibold md:font-bold">{byWhoName}</span>{' '}
                            {action === 'create' ? 'creó' : action} una solicitud
                        </>
                    );
                }
            case 'logs':
                const errorMessage = record.errorRecord || 'Error de inicio de sesión';
                return (
                    <>
                        Error de inicio de sesión: <span className="font-bold">{errorMessage}</span>
                    </>
                );
            case 'nota': {
                const parseNoteSnapshot = (raw) => {
                    if (!raw || raw === 'N/A') return null;
                    try {
                        return JSON.parse(raw);
                    } catch {
                        return null;
                    }
                };
                if (action === 'create') {
                    const snap = parseNoteSnapshot(newValue);
                    const title = snap?.title?.trim() || 'sin título';
                    return (
                        <>
                            <span className="font-bold">{byWhoName}</span> creó la nota interna{' '}
                            <span className="font-bold">{title}</span>
                        </>
                    );
                }
                if (action === 'update') {
                    const next = parseNoteSnapshot(newValue);
                    const prev = parseNoteSnapshot(previousValue);
                    const title = next?.title?.trim() || prev?.title?.trim() || 'la nota';
                    return (
                        <>
                            <span className="font-bold">{byWhoName}</span> actualizó la nota interna{' '}
                            <span className="font-bold">{title}</span>
                        </>
                    );
                }
                if (action === 'delete') {
                    const snap = parseNoteSnapshot(record.deletedValue || previousValue);
                    const title = snap?.title?.trim() || 'sin título';
                    return (
                        <>
                            <span className="font-bold">{byWhoName}</span> eliminó la nota interna{' '}
                            <span className="font-bold">{title}</span>
                        </>
                    );
                }
                break;
            }
            default:
                return (
                    <>
                        <span className="font-bold">{byWhoName}</span> realizó una acción
                    </>
                );
        }
        return (
            <>
                <span className="font-bold">{byWhoName}</span> realizó una acción
            </>
        );
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="h-full flex flex-col bg-white">

            {/* Título fijo */}
            <div className="flex-shrink-0 mb-2 lg:mb-6">
                <div className="bg-white border border-gray-200 rounded-lg px-5 py-2 lg:py-4 shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 lg:hidden">
                                    Registros
                                </h3>
                                <h3 className="text-xl font-bold text-gray-900 hidden lg:block">
                                    Registro de actividades
                                </h3>
                                <p className="text-xs text-gray-500 font-medium mt-0.5 hidden lg:block">
                                    Historial de actividades del usuario
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                            <span className="text-md text-gray-600 font-medium hidden lg:block">Total:</span>
                            <span className="text-md font-bold text-indigo-600">
                                {recordsMetadata.total || 0}
                            </span>
                            <span className="text-md text-gray-600 font-medium">actividades</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filtros: no depender de `records.length` (al filtrar puede quedar 0–1 ítems y había que poder cambiar o quitar el filtro) */}
            {(recordsMetadata.total > 0 || categoryFilter.length > 0) && (
                <div className="flex-shrink-0 mb-2 lg:mb-6">
                    <div className="bg-white border border-gray-200 rounded-md p-4 shadow-sm">
                        <div className="flex flex-col md:flex-row justify-end items-center gap-4">
                            <div className="w-full flex md:w-auto">
                                <PopoverRadioFilter
                                    value={sortOrder}
                                    onChange={setSortOrder}
                                    options={sortOptions}
                                    title='Ordenar por'
                                    subtitle='Define el orden en que se muestran las actividades.'
                                    icon={ArrowsUpDownIcon}
                                    placeholderLabel='Ordenar por'
                                />
                            </div>
                            <div className="w-full flex md:w-auto">
                                <PopoverCheckboxFilter
                                    state={categoryFilterState}
                                    setState={setCategoryFilterState}
                                    property='category'
                                    options={categoryOptions}
                                    title='Filtrar por categoría'
                                    subtitle='Selecciona una o varias categorías de actividad.'
                                    icon={Squares2X2Icon}
                                    placeholderLabel='Filtrar por categoría'
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Contenedor de actividades con scroll (misma cadena flex que notas: vacío centrado en alto) */}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-6 md:mx-2">
                {records.length > 0 ? (
                    <div className="relative">
                        {/* Línea vertical del timeline - más moderna y sutil - solo en pantallas medianas o mayores */}
                        {records.length > 1 && (
                            <div className="hidden md:block absolute left-[19px] top-[20px] bottom-[20px] w-[1px] bg-gradient-to-b from-gray-200 via-gray-300 to-gray-200"></div>
                        )}

                        {/* Lista de actividades */}
                        <div className="space-y-4">
                            {records.map((record, index) => {
                                const categoryColors = getCategoryColors(record.category);

                                return (
                                    <div key={index} className="relative flex items-center gap-4">
                                        {/* Contenedor del icono - oculto en móvil */}
                                        <div className="relative flex-shrink-0 z-10 hidden md:block">
                                            <div className={classNames(
                                                'w-10 h-10 rounded-full flex items-center justify-center',
                                                'transition-all duration-200',
                                                categoryColors.iconBg,
                                                'shadow-sm border-2 border-white',
                                                'group-hover:shadow-md group-hover:scale-105'
                                            )}>
                                                <div className={classNames(categoryColors.icon, 'w-5 h-5')}>
                                                    {getCategoryIcon(record.category)}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card del contenido de la actividad con borde */}
                                        <div className="flex-1 min-w-0 border border-gray-200 rounded-lg p-4 bg-white hover:border-gray-300 hover:shadow-sm transition-all duration-200 group">
                                            {/* Descripción */}
                                            <p className="text-[15px] text-gray-700 leading-relaxed mb-2">
                                                {getMovementDescription(record)}
                                            </p>

                                            {/* Timestamp con ícono */}
                                            <div className="flex items-center gap-1.5">
                                                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span className="text-xs text-gray-500 capitalize">
                                                    {formatDateTime(record.date || record.createdAt)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/80 px-4 py-12 text-center">
                        <div className="mb-4 rounded-full bg-gray-100 p-6">
                            <DocumentTextIcon className="h-16 w-16 text-gray-400" aria-hidden />
                        </div>
                        <h4 className="mb-2 text-lg font-semibold text-gray-700">No hay actividades registradas</h4>
                        <p className="max-w-sm px-4 text-sm text-gray-500">
                            Aún no se han registrado actividades para este usuario.
                        </p>
                    </div>
                )}
                {/* Botón "Cargar más" */}
                {recordsMetadata.hasMore && (
                    <div className="flex justify-center mt-4 md:mt-6">
                        <button
                            onClick={handleLoadMore}
                            disabled={isLoadingMore}
                            className={classNames(
                                'px-6 py-3 rounded-lg font-medium text-white',
                                'transition-all duration-200',
                                isLoadingMore
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'flex justify-center items-center gap-2 py-3.5 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg shadow-indigo-500/50 hover:shadow-xl hover:shadow-indigo-600/50 transform hover:-translate-y-0.5 transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-60 disabled:transform-none disabled:shadow-md disabled:bg-gradient-to-r disabled:from-slate-400 disabled:to-slate-500  relative overflow-hidden group'
                            )}
                        >
                            {isLoadingMore ? (
                                <div className="flex items-center gap-2">
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Cargando...</span>
                                </div>
                            ) : (
                                'Cargar más actividades'
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
